import { useEffect, useRef } from "react";
import { isNativeApp } from "../native/runtime";

const DEFAULT_EDGE_WIDTH_PX = 32;
const DEFAULT_MIN_DISTANCE_PX = 72;
const DEFAULT_MAX_VERTICAL_PX = 64;
const DEFAULT_MAX_DURATION_MS = 900;

const handlers = new Map();
let nextHandlerId = 1;
let gesture = null;
let removeGlobalListeners = null;

export function isNativeSwipeBackGesture({
  startX = 0,
  startY = 0,
  endX = 0,
  endY = 0,
  durationMs = 0,
  edgeWidth = DEFAULT_EDGE_WIDTH_PX,
  minDistance = DEFAULT_MIN_DISTANCE_PX,
  maxVertical = DEFAULT_MAX_VERTICAL_PX,
  maxDuration = DEFAULT_MAX_DURATION_MS,
} = {}) {
  const horizontalDistance = Number(endX) - Number(startX);
  const verticalDistance = Math.abs(Number(endY) - Number(startY));

  return (
    Number(startX) >= 0 &&
    Number(startX) <= edgeWidth &&
    horizontalDistance >= minDistance &&
    horizontalDistance > verticalDistance * 1.25 &&
    verticalDistance <= maxVertical &&
    Number(durationMs) >= 0 &&
    Number(durationMs) <= maxDuration
  );
}

function getTopHandler() {
  return [...handlers.values()]
    .filter((entry) => entry.enabled())
    .sort((left, right) => (
      left.priority === right.priority
        ? right.order - left.order
        : right.priority - left.priority
    ))[0] || null;
}

function installGlobalListeners() {
  if (removeGlobalListeners || typeof window === "undefined") return;

  const resetGesture = () => {
    gesture = null;
  };
  const handleTouchStart = (event) => {
    const handler = getTopHandler();
    const touch = event.touches?.length === 1 ? event.touches[0] : null;
    if (!handler || !touch || touch.clientX > handler.edgeWidth) {
      resetGesture();
      return;
    }
    if (event.target?.closest?.("[data-native-swipe-back-ignore='true']")) {
      resetGesture();
      return;
    }

    gesture = {
      handlerId: handler.id,
      startX: touch.clientX,
      startY: touch.clientY,
      endX: touch.clientX,
      endY: touch.clientY,
      startedAt: Date.now(),
      cancelled: false,
    };
  };
  const handleTouchMove = (event) => {
    if (!gesture) return;
    const touch = event.touches?.length === 1 ? event.touches[0] : null;
    if (!touch) {
      gesture.cancelled = true;
      return;
    }
    gesture.endX = touch.clientX;
    gesture.endY = touch.clientY;
    const horizontalDistance = gesture.endX - gesture.startX;
    const verticalDistance = Math.abs(gesture.endY - gesture.startY);
    if (horizontalDistance < -12 || verticalDistance > Math.max(96, Math.abs(horizontalDistance) * 1.5)) {
      gesture.cancelled = true;
    }
  };
  const handleTouchEnd = (event) => {
    const completedGesture = gesture;
    resetGesture();
    if (!completedGesture || completedGesture.cancelled) return;

    const touch = event.changedTouches?.[0];
    if (touch) {
      completedGesture.endX = touch.clientX;
      completedGesture.endY = touch.clientY;
    }
    const handler = handlers.get(completedGesture.handlerId);
    if (!handler || !handler.enabled()) return;

    if (isNativeSwipeBackGesture({
      ...completedGesture,
      durationMs: Date.now() - completedGesture.startedAt,
      edgeWidth: handler.edgeWidth,
      minDistance: handler.minDistance,
      maxVertical: handler.maxVertical,
      maxDuration: handler.maxDuration,
    })) {
      handler.onBack();
    }
  };

  window.addEventListener("touchstart", handleTouchStart, { capture: true, passive: true });
  window.addEventListener("touchmove", handleTouchMove, { capture: true, passive: true });
  window.addEventListener("touchend", handleTouchEnd, { capture: true, passive: true });
  window.addEventListener("touchcancel", resetGesture, { capture: true, passive: true });

  removeGlobalListeners = () => {
    window.removeEventListener("touchstart", handleTouchStart, true);
    window.removeEventListener("touchmove", handleTouchMove, true);
    window.removeEventListener("touchend", handleTouchEnd, true);
    window.removeEventListener("touchcancel", resetGesture, true);
    resetGesture();
    removeGlobalListeners = null;
  };
}

function uninstallGlobalListenersIfIdle() {
  if (handlers.size === 0) removeGlobalListeners?.();
}

export default function useNativeSwipeBack(
  onBack,
  {
    enabled = true,
    priority = 0,
    edgeWidth = DEFAULT_EDGE_WIDTH_PX,
    minDistance = DEFAULT_MIN_DISTANCE_PX,
    maxVertical = DEFAULT_MAX_VERTICAL_PX,
    maxDuration = DEFAULT_MAX_DURATION_MS,
  } = {}
) {
  const onBackRef = useRef(onBack);
  const enabledRef = useRef(enabled);
  onBackRef.current = onBack;
  enabledRef.current = enabled;

  useEffect(() => {
    if (!isNativeApp() || typeof window === "undefined") return undefined;

    const id = nextHandlerId++;
    handlers.set(id, {
      id,
      order: id,
      priority: Number(priority || 0),
      edgeWidth: Math.max(16, Number(edgeWidth || DEFAULT_EDGE_WIDTH_PX)),
      minDistance: Math.max(40, Number(minDistance || DEFAULT_MIN_DISTANCE_PX)),
      maxVertical: Math.max(24, Number(maxVertical || DEFAULT_MAX_VERTICAL_PX)),
      maxDuration: Math.max(250, Number(maxDuration || DEFAULT_MAX_DURATION_MS)),
      enabled: () => Boolean(enabledRef.current && onBackRef.current),
      onBack: () => onBackRef.current?.(),
    });
    installGlobalListeners();

    return () => {
      handlers.delete(id);
      uninstallGlobalListenersIfIdle();
    };
  }, [edgeWidth, maxDuration, maxVertical, minDistance, priority]);
}

